import { useCallback, useMemo } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { ExportType } from '@/types/export';
import { safeExportFileBaseName } from '@/utils/file-name';
import { useExportJob } from './useExportJob';

const ZIP_EXPORT_TOAST_ID = 'events-zip-export';
const CLIP_EXPORT_TOAST_ID = 'event-clip-export';

export const clipExportMessages = (t: TFunction) => ({
    starting: t('export.clipStarting'),
    progress: (progress: number) => t('export.clipProgress', { progress }),
    ready: t('export.clipReady'),
    readySkipped: () => t('export.clipReady'),
    failed: t('export.clipFailed'),
});

interface UseEventsExportOptions {
    exportName?: () => string;
    onStarted?: () => void;
}

export interface EventsExportControls {
    busy: boolean;
    startExport: (eventIds: string[]) => Promise<void>;
}

export function useEventsExport({
    exportName,
    onStarted,
}: UseEventsExportOptions = {}): EventsExportControls {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const zipMessages = useMemo(
        () => ({
            starting: t('export.zipStarting'),
            startingWithCount: (count: number) =>
                t('export.zipStarting', { count }),
            progress: (progress: number) =>
                t('export.zipProgress', { progress }),
            ready: t('export.zipReady'),
            readySkipped: (skippedCount: number) =>
                t('export.zipReadySkipped', { count: skippedCount }),
            failed: t('export.failed'),
        }),
        [t],
    );
    const clipMessages = useMemo(() => clipExportMessages(t), [t]);
    const zipJob = useExportJob({
        toastId: ZIP_EXPORT_TOAST_ID,
        messages: zipMessages,
    });
    const clipJob = useExportJob({
        toastId: CLIP_EXPORT_TOAST_ID,
        messages: clipMessages,
    });

    const startZip = zipJob.start;
    const startClip = clipJob.start;
    const startExport = useCallback(
        async (eventIds: string[]) => {
            if (eventIds.length === 0) {
                return;
            }
            const single = eventIds.length === 1;
            const fileBaseName = safeExportFileBaseName(
                exportName?.() ?? '',
                single ? 'event' : 'events',
            );
            const start = single ? startClip : startZip;
            const started = await start(
                ExportType.EVENTS,
                { eventIds },
                `${fileBaseName}.${single ? 'mp4' : 'zip'}`,
                eventIds.length,
            );
            if (started) {
                onStarted?.();
            }
        },
        [startZip, startClip, exportName, onStarted],
    );

    return { busy: zipJob.busy || clipJob.busy, startExport };
}
